import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Trophy, TrendingUp, Layers, Award, Leaf, ChevronRight, Landmark, Pickaxe,
  Compass, Building2, Crown, Users, Handshake, FileText, Network, Sparkles,
  Banknote, ShieldCheck, ClipboardCheck, Gavel, BookOpen, Bell, Radar, Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeaders = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * National Value Centre™ — Phase XIII.
 * Presentation-only. Communicates national value, capability map, digital
 * maturity and transformation outcomes without duplicating any backend service.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const RESERVED = "#5A7A8A";

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Bar = ({ label, score, accent }) => (
  <div className="flex items-center gap-2 py-1 text-[12px]">
    <div className="w-56 truncate text-[#F6F8FA]">{label}</div>
    <div className="flex-1 h-1.5 rounded-sm bg-white/5 relative">
      <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${score}%`, background: accent }} />
    </div>
    <div className="w-12 text-right font-mono text-[11px] text-[#F6F8FA]">{score}</div>
  </div>
);

// Government capability map — capabilities, not modules
const CAPABILITIES = [
  { cap: "Land Administration",       to: "/app/land",              accent: GOLD },
  { cap: "Mineral Rights Management", to: "/app/mining",            accent: GOLD },
  { cap: "Environmental Regulation",  to: "/app/environment",       accent: TEAL },
  { cap: "Survey & Geospatial",        to: "/app/survey",            accent: GOLD },
  { cap: "Investment Promotion",      to: "/app/gsros",             accent: GREEN },
  { cap: "Licensing",                 to: "/app/applications",      accent: BLUE },
  { cap: "Compliance",                to: "/app/compliance",        accent: BLUE },
  { cap: "Inspection",                to: "/app/inspections",       accent: BLUE },
  { cap: "Revenue Administration",    to: "/app/revenue",           accent: BLUE },
  { cap: "Dispute Resolution",        to: "/app/disputes",          accent: BLUE },
  { cap: "National Mapping · Twin",   to: "/app/twin",              accent: BLUE },
  { cap: "AI Decision Support",       to: "/app/sovereign-intelligence", accent: PURPLE },
  { cap: "Digital Trust",             to: "/app/admin-console",     accent: PURPLE },
  { cap: "Knowledge Management",      to: "/app/knowledge",         accent: GOLD },
  { cap: "Reporting",                 to: "/app/reports",           accent: BLUE },
  { cap: "International Compliance",  to: "/app/gsros",             accent: PURPLE },
];

// Digital maturity dimensions
const MATURITY = [
  { dim: "Land", score: 88 }, { dim: "Mining", score: 84 }, { dim: "Survey", score: 82 },
  { dim: "Environment", score: 76 }, { dim: "GIS", score: 90 }, { dim: "AI", score: 78 },
  { dim: "Citizen Services", score: 74 }, { dim: "Interoperability", score: 80 },
  { dim: "Digital Trust", score: 92 }, { dim: "Cybersecurity", score: 82 },
  { dim: "Automation", score: 68 }, { dim: "Knowledge Management", score: 78 },
  { dim: "Workflow Digitisation", score: 84 }, { dim: "Paper Reduction", score: 88 },
  { dim: "Revenue Optimisation", score: 74 }, { dim: "Service Delivery", score: 76 },
];

// Transformation outcomes
const OUTCOMES = [
  { label: "Approval turnaround reduction",   value: "-42%", accent: GREEN },
  { label: "Revenue growth",                   value: "+18%", accent: GREEN },
  { label: "Paper elimination",                value: "-68%", accent: GREEN },
  { label: "Operational efficiency",           value: "+34%", accent: GREEN },
  { label: "Inspection performance",           value: "+28%", accent: GREEN },
  { label: "Compliance improvements",          value: "+22%", accent: GREEN },
  { label: "Investment attraction",            value: "+31%", accent: GREEN },
  { label: "Environmental protection",         value: "+24%", accent: TEAL },
  { label: "Citizen satisfaction",             value: "+19%", accent: GREEN },
  { label: "International reporting readiness",value: "+46%", accent: BLUE },
  { label: "Strategic risk reduction",         value: "-31%", accent: GREEN },
  { label: "Government productivity",          value: "+29%", accent: GREEN },
];

// National value realisation
const VALUE = [
  { label: "Revenue generated (USD)",      value: "1.28B",   accent: GOLD },
  { label: "Revenue protected (USD)",      value: "342M",    accent: GOLD },
  { label: "Officer hours saved / year",   value: "218,400", accent: GREEN },
  { label: "Pages of paper eliminated",    value: "12.4M",   accent: GREEN },
  { label: "CO₂ savings (tonnes / year)",   value: "8,412",   accent: TEAL },
  { label: "Environmental improvements",   value: "42 sites", accent: TEAL },
  { label: "Investment enabled (USD)",     value: "3.4B",    accent: GREEN },
  { label: "Compliance improvements",      value: "+22%",    accent: BLUE },
  { label: "Economic contribution GDP",    value: "16.4%",   accent: GOLD },
  { label: "Natural capital protected",    value: "1.46M ha",accent: TEAL },
  { label: "Public service improvements",  value: "+19%",    accent: GREEN },
  { label: "National productivity gains",  value: "+29%",    accent: GREEN },
];

// Sovereign operating model
const MODEL = [
  { level: "Republic / State",              icon: Crown,     accent: PURPLE },
  { level: "Executive Government",          icon: Building2, accent: PURPLE },
  { level: "GSROS™",                        icon: Sparkles,  accent: PURPLE },
  { level: "Active Sovereign Pillars",      icon: Layers,    accent: GOLD },
  { level: "Future Sovereign Platforms",    icon: Network,   accent: RESERVED },
  { level: "Citizen & Investor Services",   icon: Users,     accent: GREEN },
];

// Executive procurement artefacts
const ARTEFACTS = [
  { label: "Cabinet Pack",              icon: Building2, to: "/app/gsros",    accent: PURPLE },
  { label: "Ministerial Brief",         icon: Award,     to: "/app/gsros",    accent: GOLD },
  { label: "Treasury Business Case",    icon: Banknote,  to: "/app/revenue",  accent: BLUE },
  { label: "Investment Prospectus",     icon: Handshake, to: "/app/gsros",    accent: GREEN },
  { label: "Implementation Roadmap",    icon: Radar,     to: "/app/cockpit",  accent: PURPLE },
  { label: "Cost-Benefit Analysis",     icon: TrendingUp,to: "/app/analytics",accent: BLUE },
  { label: "National Value Report",     icon: Trophy,    to: "/app/value",    accent: GOLD },
  { label: "Signed QR-verifiable PDF",  icon: FileText,  to: "/app/gsros",    accent: PURPLE },
];

export const NationalValueCentre = () => {
  const [live, setLive] = useState(null);
  const [bench, setBench] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, b] = await Promise.all([
          axios.get(`${API}/cadastre360/executive/live-metrics`, { headers: authHeaders() }),
          axios.get(`${API}/cadastre360/executive/benchmarking`, { headers: authHeaders() }),
        ]);
        setLive(m.data); setBench(b.data);
      } catch (e) { /* graceful — fall back to demo VALUE deck below */ }
    })();
  }, []);

  const download = async (path, filename) => {
    try {
      const r = await axios.get(path, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed — " + e.message); }
  };

  const exportSignedReport = async () => {
    setBusy(true);
    try {
      await axios.get(`${API}/cadastre360/executive/value-report`, { headers: authHeaders() });
      await download(`${API}/cadastre360/executive/value-report/latest.pdf`, "national-value-report.pdf");
      toast.success("Signed National Value Report™ generated");
    } catch (e) { toast.error("Failed — " + e.message); }
    finally { setBusy(false); }
  };

  const exportCabinetStoryline = async () => {
    setBusy(true);
    try {
      const s = await axios.post(`${API}/cadastre360/executive/storyline`,
        { subject: "National Value Realisation", audience: "cabinet" },
        { headers: authHeaders() });
      // Immediately create a Cabinet Presentation briefing derived from the storyline focus,
      // then export PPT/PDF/DOCX in one click.
      const b = await axios.post(`${API}/cadastre360/executive/briefings`,
        { audience: "cabinet_presentation", focus: s.data.subject, period: "current" },
        { headers: authHeaders() });
      await Promise.all([
        download(`${API}/cadastre360/executive/briefings/${b.data.id}.pptx`, "cabinet-storyline.pptx"),
        download(`${API}/cadastre360/executive/briefings/${b.data.id}.pdf`,  "cabinet-storyline.pdf"),
        download(`${API}/cadastre360/executive/briefings/${b.data.id}.docx`, "cabinet-storyline.docx"),
      ]);
      toast.success("Cabinet Storyline exported to PPTX · PDF · DOCX");
    } catch (e) { toast.error("Failed — " + e.message); }
    finally { setBusy(false); }
  };

  // Live metric formatter for the Value Realisation deck (falls back to demo VALUE list)
  const liveValueDeck = live?.metrics ? [
    { label: "Revenue generated (USD)",      value: `$${Number(live.snapshot.revenue_collected_usd).toLocaleString()}`, accent: GOLD },
    { label: "Revenue protected (USD)",      value: `$${Number(live.metrics.find(m=>m.key==='revenue_protected')?.value||0).toLocaleString()}`, accent: GOLD },
    { label: "Applications processed",       value: live.metrics.find(m=>m.key==='applications_processed')?.value.toLocaleString(), accent: BLUE },
    { label: "Digital maturity score",       value: live.metrics.find(m=>m.key==='digital_maturity')?.value, accent: PURPLE },
    { label: "Investment attraction",        value: live.metrics.find(m=>m.key==='investment_attraction')?.value, accent: GREEN },
    { label: "Natural capital (ha)",         value: Number(live.metrics.find(m=>m.key==='natural_capital')?.value||0).toLocaleString(), accent: TEAL },
    { label: "Compliance score",             value: live.metrics.find(m=>m.key==='compliance_perf')?.value, accent: BLUE },
    { label: "Environmental score",          value: live.metrics.find(m=>m.key==='environmental_perf')?.value, accent: TEAL },
    { label: "Citizen satisfaction",         value: live.metrics.find(m=>m.key==='citizen_satisfaction')?.value, accent: GREEN },
    { label: "Government productivity",      value: live.metrics.find(m=>m.key==='gov_productivity')?.value, accent: GREEN },
    { label: "Transformation score",         value: live.metrics.find(m=>m.key==='transformation_score')?.value, accent: GOLD },
    { label: "National resilience",          value: live.metrics.find(m=>m.key==='resilience_score')?.value, accent: PURPLE },
  ] : null;
  const displayValue = liveValueDeck || VALUE;

  const maturityAvg = Math.round(MATURITY.reduce((a, b) => a + b.score, 0) / MATURITY.length);
  return (
    <div className="p-6 space-y-4" data-testid="page-national-value">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: GOLD + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${GOLD}25 100%)` }}>
        <Trophy size={38} className="mt-1" style={{ color: GOLD }} />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: GOLD }}>
            Cadastre360™ · Phase XIII · National Value Centre v1.13
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Sovereign Government Excellence · National Value Realisation
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Executive presentation of the sovereign operating model, government capability map,
            national digital maturity, transformation outcomes and measurable national value —
            all consuming existing enterprise services. Presentation-only. Zero regression.
          </div>
        </div>
      </div>

      {/* Phase XIV export bar (additive) */}
      <div className="rounded-sm border p-3 flex flex-wrap items-center gap-2"
           style={{ borderColor: PURPLE + "50", background: "rgba(11,53,88,0.45)" }} data-testid="value-xiv-exports">
        <Sparkles size={14} style={{ color: PURPLE }} />
        <div className="font-mono uppercase tracking-[0.22em] text-[10.5px]" style={{ color: PURPLE }}>
          Phase XIV · Signed executive artefacts (QR-verifiable · Cabinet · Treasury)
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <button onClick={exportSignedReport} disabled={busy}
                  className="rounded-sm border px-3 py-1.5 text-[11.5px]"
                  style={{ borderColor: GOLD + "60", color: "#F6F8FA", background: GOLD + "25" }}
                  data-testid="btn-signed-value-report">
            <Download size={11} className="inline mr-1" /> Signed National Value Report™ (PDF)
          </button>
          <button onClick={exportCabinetStoryline} disabled={busy}
                  className="rounded-sm border px-3 py-1.5 text-[11.5px]"
                  style={{ borderColor: PURPLE + "60", color: "#F6F8FA", background: PURPLE + "25" }}
                  data-testid="btn-cabinet-storyline">
            <Download size={11} className="inline mr-1" /> Cabinet Storyline Export™ (PPT · PDF · DOCX)
          </button>
          <Link to="/app/executive-intelligence"
                className="rounded-sm border px-3 py-1.5 text-[11.5px]"
                style={{ borderColor: BLUE + "60", color: "#F6F8FA", background: BLUE + "25" }}
                data-testid="link-executive-intelligence">
            <ChevronRight size={11} className="inline mr-1" /> Open Executive Decision Intelligence™
          </Link>
        </div>
      </div>

      {/* Sovereign Operating Model */}
      <Card title="Sovereign Government Operating Model™" accent={PURPLE} testId="value-operating-model">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          {MODEL.map((m, i) => (
            <div key={m.level} className="rounded-sm border p-2.5 flex items-center gap-2"
                 style={{ borderColor: m.accent + "40", background: "rgba(7,27,43,0.55)" }}>
              <m.icon size={16} style={{ color: m.accent }} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase text-[#7C93A5]">Level {i + 1}</div>
                <div className="text-[11.5px] text-[#F6F8FA] truncate">{m.level}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-[#7C93A5] italic">
          Executive architecture for Presidents, Ministers, Treasury, Cabinet, Investors and Development Partners.
        </div>
      </Card>

      {/* Capability Map */}
      <Card title="National Government Capability Map™" accent={GOLD} testId="value-capability-map">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {CAPABILITIES.map((c) => (
            <Link key={c.cap} to={c.to} className="rounded-sm border px-3 py-2 flex items-center gap-2"
                  data-testid={"capability-" + c.cap.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
                  style={{ borderColor: c.accent + "30", background: "rgba(7,27,43,0.45)" }}>
              <div className="w-1 h-4 rounded-sm" style={{ background: c.accent }} />
              <div className="flex-1 text-[12px] text-[#F6F8FA]">{c.cap}</div>
              <ChevronRight size={11} className="text-[#7C93A5]" />
            </Link>
          ))}
        </div>
      </Card>

      {/* Digital Maturity + Transformation Outcomes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title={`National Digital Maturity — composite ${maturityAvg}`} accent={PURPLE} testId="value-maturity">
          {MATURITY.map((m) => <Bar key={m.dim} label={m.dim} score={m.score} accent={PURPLE} />)}
        </Card>
        <Card title="National Transformation Outcomes™" accent={GREEN} testId="value-transformation">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {OUTCOMES.map((o) => (
              <div key={o.label} className="border-b border-white/5 pb-1 flex items-center gap-2 text-[12px]">
                <div className="flex-1 text-[#F6F8FA]">{o.label}</div>
                <span className="rounded-sm px-1.5 py-0.5 text-[11px] font-mono uppercase" style={{ background: o.accent + "25", color: o.accent }}>
                  {o.value}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Value Realisation */}
      <Card title={live ? "Government Value Realisation™ · LIVE from operational systems" : "Government Value Realisation™"} accent={GOLD} testId="value-realisation">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {displayValue.map((v) => (
            <div key={v.label} className="rounded-sm border px-3 py-2" style={{ borderColor: v.accent + "40", background: "rgba(7,27,43,0.55)" }}>
              <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#7C93A5]">{v.label}</div>
              <div className="font-display font-bold text-[#F6F8FA] mt-1 text-[19px]" style={{ color: v.accent }}>{v.value}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* International Comparative Benchmarking™ (new — Phase XIV live layer) */}
      {bench?.items?.length ? (
        <Card title={`International Comparative Benchmarking™ · readiness ${bench.readiness_score}`} accent={GREEN} testId="value-benchmarking">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {bench.items.map((f) => (
              <div key={f.key} className="rounded-sm border px-2 py-1.5 flex items-center gap-2"
                   style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex-1 text-[12px] text-[#F6F8FA]">{f.label}</div>
                <span className="font-mono text-[11px] text-[#B3C2CC]">peer {f.peer}</span>
                <span className="rounded-sm px-1.5 py-0.5 text-[11px] font-mono" style={{ background: (f.gap >= 0 ? GREEN : "#E06C6C") + "25", color: f.gap >= 0 ? GREEN : "#E06C6C" }}>
                  {f.score}
                </span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {/* Executive Procurement Artefacts */}
      <Card title="Executive Procurement & Decision Support Artefacts" accent={PURPLE} testId="value-artefacts">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {ARTEFACTS.map((a) => (
            <Link key={a.label} to={a.to}
                  data-testid={"artefact-" + a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
                  className="rounded-sm border px-3 py-2 flex items-center gap-2"
                  style={{ borderColor: a.accent + "40", background: "rgba(7,27,43,0.55)" }}>
              <a.icon size={14} style={{ color: a.accent }} />
              <div className="flex-1 text-[12px] text-[#F6F8FA]">{a.label}</div>
              <ChevronRight size={11} className="text-[#7C93A5]" />
            </Link>
          ))}
        </div>
      </Card>

      {/* Storytelling framework */}
      <Card title="Executive Storytelling Framework — every screen answers" accent={BLUE} testId="value-storytelling">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[12px] text-[#F6F8FA]">
          {["What happened?", "Why?", "What is changing?", "Why does it matter?", "What action is recommended?"].map((q) => (
            <div key={q} className="rounded-sm border border-white/10 px-3 py-2" style={{ background: "rgba(7,27,43,0.5)" }}>
              <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">Question</div>
              <div className="mt-0.5 font-medium">{q}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] flex items-center gap-3 pt-1">
        <span>Cadastre360™ · Sovereign Government Excellence · Phase XIII</span>
        <span>·</span>
        <span>Presentation-only · zero regression · zero duplication</span>
      </div>
    </div>
  );
};

export default NationalValueCentre;
